/**
 * §H4 suitability — "objective preset select (cosmetic label); 5 weight sliders … with live
 * sum badge (green when 100); output level select"; result "top-8 ranked table (bold mono
 * scores), suitability result choropleth (green ramp), note re provisional SFI joining
 * post-validation, full-ranking CSV".
 *
 * **The sum badge does not gate the button.** §H4 normalises by `Σw`, so weights of
 * 30/25/20/15/10 and 60/50/40/30/20 are the same analysis; the badge turns green at 100
 * because that is the reading a person can compare against the defaults, not because
 * anything else is invalid. Refusing to run on 99 would be inventing a rule the engine
 * does not have.
 *
 * **The criteria table is the run's, not the form's.** `criteria_used` lists only the
 * criteria that were actually read, each with the **run's** weight (T5 fix 2 — it used to
 * serve the default), its resolved dataset version and its direction. "(inverse)" is
 * rendered off `direction`, never off the label, so a translated label cannot lose it.
 *
 * A `null` in `rows[].criteria` is the missing-data list: §H4 deliberately serves no
 * separate `missing` array, so the panel prints the em dash and the footnote counts them.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToolRun, type SuitabilityResult } from '../../../lib/analysis';
import { EMDASH, fmtNum } from '../../../lib/format';
import { Icon } from '../../../lib/icons';
import { selectScopeParent, useAppStore } from '../../../lib/state';
import { toast } from '../../../lib/toast';
import type { ToolProps } from '../AnalysisDock';
import { ToolFrame } from '../ToolFrame';
import { ResultBox, ResultNote } from '../ResultBox';
import { downloadCsv, toCsv } from '../csv';

/** `suit.CRITERIA` — the keys, their default weights and the slider order (§H4). */
export const CRITERIA = [
  { key: 'soc', weight: 30 },
  { key: 'rain', weight: 25 },
  { key: 'lc', weight: 20 },
  { key: 'water', weight: 15 },
  { key: 'erosion', weight: 10 },
] as const;

export const DEFAULT_WEIGHTS: Record<string, number> = Object.fromEntries(
  CRITERIA.map((c) => [c.key, c.weight]),
);

/** The mockup's three objectives. Cosmetic per §H4 — the model is the weights. */
export const OBJECTIVES = ['isfm', 'agroforestry', 'restoration'] as const;

/** Slider range — the mockup's `max=50`, which the defaults all sit inside. */
export const MAX_WEIGHT = 50;

/** §H4's ranked table length. */
export const TOP_N = 8;

const LEVELS = [
  { value: '1', key: 'indicators.levelRegion' },
  { value: '2', key: 'indicators.levelCommune' },
  { value: '0', key: 'indicators.levelCountry' },
];

export function Suitability({ onBack }: ToolProps) {
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const parent = useAppStore((s) => selectScopeParent(s));
  const addResultLayer = useAppStore((s) => s.addResultLayer);

  const [objective, setObjective] = useState<string>(OBJECTIVES[0]);
  const [weights, setWeights] = useState<Record<string, number>>({ ...DEFAULT_WEIGHTS });
  const [level, setLevel] = useState('1');
  const run = useToolRun<SuitabilityResult>('suitability');

  const total = useMemo(
    () => CRITERIA.reduce((sum, c) => sum + (weights[c.key] ?? 0), 0),
    [weights],
  );

  const submit = () => {
    if (total <= 0) {
      toast(t('analysis.suitability.noWeights'));
      return;
    }
    run.run({
      weights,
      level: Number(level),
      ...(country === 'ALL' ? {} : { country }),
      ...(parent ? { parent } : {}),
    });
  };

  const result = run.result;

  const style = () => {
    const spec = result?.result_layers?.[0];
    if (!spec) return;
    addResultLayer(spec, {
      jobId: run.jobId ?? spec.id,
      tool: 'suitability',
      label: t('analysis.suitability.layerLabel'),
    });
    toast(t('analysis.suitability.styled'));
  };

  const exportCsv = () => {
    if (!result) return;
    const keys = result.criteria_used.map((c) => c.key);
    const header = [
      t('analysis.csv.rank'),
      t('analysis.csv.unit'),
      t('analysis.csv.country'),
      t('analysis.suitability.score'),
      ...keys,
    ];
    const rows = result.rows.map((row, i) => [
      i + 1,
      row.name,
      row.country ?? '',
      row.score,
      ...keys.map((key) => row.criteria[key] ?? null),
    ]);
    downloadCsv('suitability_ranking.csv', toCsv(header, rows));
  };

  return (
    <ToolFrame
      tool="suitability"
      onBack={onBack}
      phase={run.phase}
      progress={run.progress}
      error={run.error}
      cached={run.cached}
      result={
        result ? (
          <>
            <ResultBox title={t('analysis.suitability.ranking', { n: TOP_N })}>
              <table className="mini">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('analysis.csv.unit')}</th>
                    <th className="num">{t('analysis.suitability.scoreCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.slice(0, TOP_N).map((row, i) => (
                    <tr key={row.unit_id}>
                      <td>{i + 1}</td>
                      <td>
                        {row.name} <small style={{ color: 'var(--ink-3)' }}>{row.country ?? ''}</small>
                      </td>
                      <td className="num">
                        <b>{row.score === null ? EMDASH : fmtNum(row.score, 0)}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ResultBox>

            <ResultBox title={t('analysis.suitability.criteriaUsed')}>
              <table className="mini">
                <tbody>
                  {result.criteria_used.map((c) => (
                    <tr key={c.key}>
                      <td>
                        {t(`analysis.suitability.criteria.${c.key}`, c.label)}
                        {c.direction === 'inverse' ? ` ${t('analysis.suitability.inverse')}` : ''}
                      </td>
                      <td className="num">{c.weight}</td>
                      <td style={{ color: 'var(--ink-3)', fontSize: 10.5 }}>
                        {c.dataset}@{c.version}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ResultNote>
                {t('analysis.suitability.foot', {
                  n: fmtNum(result.n_units, 0),
                  total: result.weight_total,
                })}
              </ResultNote>
            </ResultBox>

            {(result.notes ?? []).map((note) => (
              <ResultNote key={note}>{t(`analysis.note.${note}`, note)}</ResultNote>
            ))}

            <div className="ad-actions">
              {result.result_layers.length ? (
                <button type="button" className="btn primary sm" id="suitMap" onClick={style}>
                  <Icon name="map" size={12} /> {t('analysis.suitability.style')}
                </button>
              ) : null}
              <button type="button" className="btn ghost sm" id="suitCsv" onClick={exportCsv}>
                <Icon name="download" size={12} /> {t('analysis.suitability.csv')}
              </button>
            </div>
          </>
        ) : null
      }
    >
      <label className="fld" htmlFor="suitObj">
        {t('analysis.suitability.objective')}
      </label>
      <select className="std" id="suitObj" value={objective} onChange={(e) => setObjective(e.target.value)}>
        {OBJECTIVES.map((key) => (
          <option key={key} value={key}>
            {t(`analysis.suitability.objectives.${key}`)}
          </option>
        ))}
      </select>

      <label className="fld">
        {t('analysis.suitability.weights')}{' '}
        <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>
          {t('analysis.suitability.sum')}{' '}
          <b className={`wsum${total === 100 ? ' ok' : ''}`} id="wSum" data-sum={total}>
            {total}
          </b>
          %
        </span>
      </label>
      {CRITERIA.map((c) => (
        <div className="weight-row" key={c.key}>
          <span className="wname" id={`w-${c.key}`}>
            {t(`analysis.suitability.criteria.${c.key}`)}
          </span>
          <input
            type="range"
            min={0}
            max={MAX_WEIGHT}
            value={weights[c.key] ?? 0}
            aria-labelledby={`w-${c.key}`}
            data-w={c.key}
            onChange={(e) => setWeights((w) => ({ ...w, [c.key]: Number(e.target.value) }))}
          />
          <span className="wval" data-wv={c.key}>
            {weights[c.key] ?? 0}
          </span>
        </div>
      ))}

      <label className="fld" htmlFor="suitLv">
        {t('analysis.suitability.level')}
      </label>
      <select className="std" id="suitLv" value={level} onChange={(e) => setLevel(e.target.value)}>
        {LEVELS.map((lv) => (
          <option key={lv.value} value={lv.value}>
            {t(lv.key)}
          </option>
        ))}
      </select>

      <button
        type="button"
        className="btn primary ad-run"
        id="suitRun"
        disabled={run.phase === 'running'}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('analysis.suitability.run')}
      </button>
    </ToolFrame>
  );
}

export default Suitability;
